/**
 * TextureOptimizer - Browser-based texture resizing and optimization
 * Auto-generates multi-resolution variants for desktop/mobile/tablet/VR
 * Supports KTX2 compression for 50-70% smaller files
 */

import { Logger } from './Logger';
import { BasisEncoderWrapper } from './BasisEncoderWrapper';

const logger = Logger.getLogger('TextureOptimizer');

// Variant name type
type VariantName = 'desktop' | 'mobile' | 'tablet' | 'vr' | 'fallback';

export interface OptimizedVariant {
  name: VariantName;
  blob: Blob;
  width: number;
  height: number;
  format: 'jpg' | 'ktx2-uastc' | 'ktx2-etc1s';
  size: number;
}

export interface VariantSpec {
  width: number;
  height: number;
  quality: number;
  format: 'jpg' | 'ktx2-uastc' | 'ktx2-etc1s';
}

export class TextureOptimizer {
  // Dual spec sets: KTX2 enabled vs disabled
  private static specsJPG: Record<string, VariantSpec> = {
    desktop: { width: 8192, height: 4096, quality: 0.92, format: 'jpg' },      // 8K JPG, ~8 MB
    mobile: { width: 4096, height: 2048, quality: 0.88, format: 'jpg' },       // 4K JPG, ~3 MB
    tablet: { width: 4096, height: 2048, quality: 0.90, format: 'jpg' },       // 4K JPG, ~3.5 MB
    vr: { width: 8192, height: 4096, quality: 0.92, format: 'jpg' },           // 8K JPG, ~8 MB
    fallback: { width: 2048, height: 1024, quality: 0.85, format: 'jpg' }      // 2K JPG, ~600 KB
  };

  private static specsKTX2: Record<string, VariantSpec> = {
    desktop: { width: 8192, height: 4096, quality: 0.92, format: 'jpg' },          // 8K JPG, ~8 MB
    mobile: { width: 2048, height: 1024, quality: 128, format: 'ktx2-etc1s' },     // 2K KTX2, ~400 KB
    tablet: { width: 2048, height: 1024, quality: 192, format: 'ktx2-etc1s' },     // 2K KTX2, ~500 KB
    vr: { width: 8192, height: 4096, quality: 0.92, format: 'jpg' },               // 8K JPG, ~8 MB
    fallback: { width: 2048, height: 1024, quality: 0.85, format: 'jpg' }          // 2K JPG, ~600 KB
  };

  private static encoderInitialized: boolean = false;

  /**
   * Auto-generate all optimized variants from a source image
   * @param file Source image file
   * @param useKTX2 Enable KTX2 compression for mobile/tablet (default: false)
   * @param onProgress Callback for progress updates (variant, progress)
   * @returns Array of optimized variants ready for upload
   */
  static async generateAllVariants(
    file: File,
    useKTX2: boolean = false,
    onProgress?: (variant: string, progress: number) => void
  ): Promise<OptimizedVariant[]> {
    logger.info(`Starting optimization: ${file.name} (KTX2: ${useKTX2 ? 'ON' : 'OFF'})`);

    // Select spec set based on KTX2 preference
    const specs = useKTX2 ? this.specsKTX2 : this.specsJPG;

    // Initialize Basis encoder (only if using KTX2)
    if (useKTX2 && !this.encoderInitialized) {
      try {
        await BasisEncoderWrapper.loadModule();
        this.encoderInitialized = true;
      } catch (error) {
        logger.warn('KTX2 encoder failed to load, falling back to JPG:', error);
        // Continue with JPG encoding
      }
    }

    // Load source image
    const sourceImage = await this.loadImage(file);
    logger.debug(`Source loaded: ${sourceImage.width}×${sourceImage.height}`);

    const variants: OptimizedVariant[] = [];

    // Generate each variant
    for (const [variantName, spec] of Object.entries(specs)) {
      onProgress?.(variantName, 0);

      try {
        const variant = await this.generateVariant(
          sourceImage,
          variantName as VariantName,
          spec
        );
        variants.push(variant);

        onProgress?.(variantName, 100);
        logger.info(
          `Generated ${variantName}: ${variant.width}×${variant.height} ${variant.format.toUpperCase()}, ${this.formatBytes(variant.size)}`
        );
      } catch (error) {
        logger.error(`Failed to generate ${variantName}:`, error);
        onProgress?.(variantName, -1); // Signal error
      }
    }

    logger.info(`Generated ${variants.length} variants`);
    return variants;
  }

  /**
   * Generate a single optimized variant
   */
  private static async generateVariant(
    sourceImage: HTMLImageElement,
    name: 'desktop' | 'mobile' | 'tablet' | 'vr' | 'fallback',
    spec: VariantSpec
  ): Promise<OptimizedVariant> {
    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = spec.width;
    canvas.height = spec.height;

    // For KTX2 encoding, we need alpha channel (PNG requirement)
    const needsAlpha = spec.format.startsWith('ktx2');
    const ctx = canvas.getContext('2d', { alpha: needsAlpha });

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // High-quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw resized image
    ctx.drawImage(sourceImage, 0, 0, spec.width, spec.height);

    // Verify canvas has data
    logger.debug(`Canvas created: ${canvas.width}×${canvas.height}, has data: ${ctx.getImageData(0, 0, 1, 1).data.length > 0}`);

    let blob: Blob;
    let actualFormat: OptimizedVariant['format'];

    // Encode based on format
    if (spec.format.startsWith('ktx2') && this.encoderInitialized) {
      try {
        // KTX2 encoding
        const ktx2Data = await BasisEncoderWrapper.encodeCanvasToKTX2(canvas, {
          format: spec.format === 'ktx2-uastc' ? 'uastc' : 'etc1s',
          quality: spec.quality,
          generateMipmaps: true,
          srgb: true
        });

        blob = new Blob([ktx2Data.buffer as ArrayBuffer], { type: 'image/ktx2' });
        actualFormat = spec.format;

      } catch (error) {
        logger.warn(`KTX2 encoding failed for ${name}, falling back to JPG:`, error);
        // Fallback to JPG
        const jpgBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (b) => b ? resolve(b) : reject(new Error('JPG encoding failed')),
            'image/jpeg',
            0.92
          );
        });
        blob = jpgBlob;
        actualFormat = 'jpg';
      }
    } else {
      // JPG encoding (fallback variant or encoder not loaded)
      const jpgQuality = typeof spec.quality === 'number' && spec.quality < 1 ? spec.quality : 0.85;
      blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => b ? resolve(b) : reject(new Error('JPG encoding failed')),
          'image/jpeg',
          jpgQuality
        );
      });
      actualFormat = 'jpg';
    }

    return {
      name,
      blob,
      width: spec.width,
      height: spec.height,
      format: actualFormat,
      size: blob.size
    };
  }

  /**
   * Load image from file
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Format bytes to human-readable string
   */
  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i];
  }

  /**
   * Calculate total savings from optimization
   */
  static calculateSavings(originalSize: number, variants: OptimizedVariant[]): {
    totalSize: number;
    savingsBytes: number;
    savingsPercent: number;
  } {
    const totalSize = variants.reduce((sum, v) => sum + v.size, 0);
    const savingsBytes = originalSize - totalSize;
    const savingsPercent = (savingsBytes / originalSize) * 100;

    return { totalSize, savingsBytes, savingsPercent };
  }
}
